import { useReducer, useCallback } from 'react';
import type {
  ImageEditorState,
  ImageEditorAction,
  EditorTool,
  AICapability,
  AdjustmentConfig,
  BrushSettings,
  BlendMode,
  LayerEffects,
  LayerMask,
  LayerNode,
  TextPathBinding,
  PenSettings,
  TextSettings,
  ShapeSettings,
  MarqueeSettings,
  RetouchSettings,
  RetouchSettingsPatch,
  CropState,
  Layer,
  EditorDocument,
  VectorAnchor,
  VectorPath,
} from '../types';
import {
  emptyDocument,
  fromFlatLayers,
  toOrderedList,
  addNode,
  removeNode,
  updateNode,
  reorderFlattened,
  setMask,
  groupNodes,
  ungroupNode,
  moveNode,
} from '../lib/layer-tree';

/** Wrap a document update so the derived flat `layers` view stays
 *  in lockstep with `document`. Every reducer case that mutates
 *  layers should route through this.
 *
 *  Returns the SAME `state` reference when the document didn't
 *  change — preserves referential identity for no-op actions
 *  (e.g. `REORDER_LAYER` with `fromIndex === toIndex`) which the
 *  layer-tree helpers already short-circuit. Downstream consumers
 *  use the state object in `useEffect` dep arrays. */
function withDocument(state: ImageEditorState, document: EditorDocument): ImageEditorState {
  if (document === state.document) return state;
  return { ...state, document, layers: toOrderedList(document) };
}

const initialState: ImageEditorState = {
  activeTool: 'move',
  brushSettings: { color: '#000000', width: 4, opacity: 1 },
  textSettings: {
    fontFamily: 'Arial',
    fontSize: 48,
    fontWeight: 'normal',
    fontStyle: 'normal',
    underline: false,
    textAlign: 'left',
    fill: '#000000',
    arch: 0,
    // Tier 2 Group B defaults — match Fabric's natural rendering:
    // charSpacing 0 (no tracking), lineHeight 1.16 (Fabric's
    // default), no stroke, no drop shadow. Sessions persisted before
    // Tier 2 Group B don't carry these fields; the reducer's
    // UPDATE_TEXT_SETTINGS spreads onto whatever defaults are here
    // so old sessions silently pick them up on first edit.
    letterSpacing: 0,
    lineHeight: 1.16,
  },
  shapeSettings: {
    type: 'rect',
    fill: 'transparent',
    stroke: '#000000',
    strokeWidth: 2,
    cornerRadius: 0,
  },
  marqueeSettings: {
    shape: 'rect',
    forceSquare: false,
    color: '#0078d7',
    tolerance: 32,
    contiguous: true,
  },
  retouchSettings: {
    variant: 'clone',
    size: 30,
    hardness: 50,
    opacity: 100,
    flow: 100,
    smoothing: 0,
    pressureSize: false,
    pressureOpacity: false,
    pressureFlow: false,
    clone: { sample: 'current', aligned: true },
    heal: { mode: 'normal' },
    smudge: { strength: 50, fingerPainting: false },
    blur: { strength: 50 },
    sharpen: { strength: 50 },
    dodge: { range: 'midtones', exposure: 50 },
    burn: { range: 'midtones', exposure: 50 },
    sponge: { mode: 'saturate', flow: 50 },
  },
  document: emptyDocument(),
  layers: [],
  activeLayerId: null,
  editTargets: {},
  layersPanelOpen: true,
  crop: { isActive: false, aspectRatio: null, rect: null },
  zoom: 1,
  canvasWidth: 0,
  canvasHeight: 0,
  selectedObjectIds: [],
  fileName: 'Untitled',
  fileId: null,
  isDirty: false,
  propertiesPanelOpen: true,
  selectedAITool: null,
  aiPickerOpen: false,
  quickMaskActive: false,
  quickMaskPrevTool: null,
  quickMaskMode: 'add',
  transformActive: false,
  transformMode: 'free',
  transformLayerId: null,
  penSettings: {
    variant: 'curvature',
    autoAddDelete: true,
    strokeColor: '#000000',
    strokeWidth: 2,
    fill: 'transparent',
    freeformMagnetic: false,
    freeformWidth: 50,
  },
  pathEditActive: false,
  pathEditLayerId: null,
  pathEditTarget: 'layer',
  pathEditSelectedAnchorIndex: null,
};

function reducer(state: ImageEditorState, action: ImageEditorAction): ImageEditorState {
  switch (action.type) {
    case 'SET_ACTIVE_TOOL':
      return {
        ...state,
        activeTool: action.tool,
        // Deactivate crop when switching away
        crop: action.tool !== 'crop' ? { ...state.crop, isActive: false } : state.crop,
        // Closing the AI tool also closes the picker — prevents the
        // dialog from sticking around in the background after the user
        // moves on to a different tool.
        aiPickerOpen: action.tool === 'ai' ? state.aiPickerOpen : false,
      };
    case 'SET_SELECTED_AI_TOOL':
      return { ...state, selectedAITool: action.tool };
    case 'SET_AI_PICKER_OPEN':
      return { ...state, aiPickerOpen: action.open };
    case 'SET_QUICK_MASK_MODE':
      if (state.quickMaskMode === action.mode) return state;
      return { ...state, quickMaskMode: action.mode };
    case 'BEGIN_TRANSFORM':
      // Re-entering Transform on the same layer with a fresh BEGIN
      // is a no-op — the canvas-side capture isn't reset and the
      // user's in-progress edits stay. Re-entering on a DIFFERENT
      // layer ends the prior session implicitly (caller is
      // expected to commit/cancel first; this is a defensive
      // guard, not the happy path).
      if (state.transformActive && state.transformLayerId === action.layerId) {
        return state;
      }
      return {
        ...state,
        transformActive: true,
        transformLayerId: action.layerId,
        transformMode: 'free',
        // Clear adjacent modes so the user isn't in two modal
        // interactions at once. Quick Mask wins if both were
        // requested but in practice the trigger paths can't
        // overlap (BEGIN_TRANSFORM goes through layer overflow or
        // Ctrl+T; QM goes through the StatusBar chip).
        crop: { ...state.crop, isActive: false },
        aiPickerOpen: false,
      };
    case 'SET_TRANSFORM_MODE':
      if (!state.transformActive) return state;
      if (state.transformMode === action.mode) return state;
      return { ...state, transformMode: action.mode };
    case 'END_TRANSFORM':
      if (!state.transformActive) return state;
      return {
        ...state,
        transformActive: false,
        transformLayerId: null,
        transformMode: 'free',
      };
    case 'UPDATE_PEN_SETTINGS':
      return { ...state, penSettings: { ...state.penSettings, ...action.settings } };
    case 'BEGIN_PATH_EDIT': {
      // Same atomic-entry pattern as BEGIN_TRANSFORM. Re-entering
      // on the same layer + same target is a no-op; re-entering
      // on a different layer or switching target ends the prior
      // session implicitly (caller is expected to commit/cancel
      // first; defensive guard).
      const target = action.target ?? 'layer';
      if (
        state.pathEditActive &&
        state.pathEditLayerId === action.layerId &&
        state.pathEditTarget === target
      ) {
        return state;
      }
      const node = state.document.layers[action.layerId];
      if (!node) return state;
      // 'layer' target requires a vector layer; 'mask' target
      // requires a vector mask on the layer. No-op when the
      // layer doesn't match.
      if (target === 'layer' && node.type !== 'vector') return state;
      if (target === 'mask') {
        const mask = node.mask;
        if (!mask || mask.kind !== 'vector') return state;
      }
      return {
        ...state,
        pathEditActive: true,
        pathEditLayerId: action.layerId,
        pathEditTarget: target,
        pathEditSelectedAnchorIndex: null,
        crop: { ...state.crop, isActive: false },
        aiPickerOpen: false,
      };
    }
    case 'SET_PATH_EDIT_ANCHOR':
      if (!state.pathEditActive) return state;
      if (state.pathEditSelectedAnchorIndex === action.index) return state;
      return { ...state, pathEditSelectedAnchorIndex: action.index };
    case 'END_PATH_EDIT':
      if (!state.pathEditActive) return state;
      return {
        ...state,
        pathEditActive: false,
        pathEditLayerId: null,
        pathEditTarget: 'layer',
        pathEditSelectedAnchorIndex: null,
      };
    case 'SET_VECTOR_PATH': {
      const node = state.document.layers[action.id];
      if (!node || node.type !== 'vector') return state;
      const patch: Partial<LayerNode> = { path: action.path };
      if (action.clearVectorSource) patch.vectorSource = undefined;
      return withDocument(state, updateNode(state.document, action.id, patch));
    }
    case 'CONVERT_LAYER_TO_PAINT': {
      const node = state.document.layers[action.id];
      if (!node) return state;
      // Already paint / image / empty / etc. — nothing to convert
      // and we don't want to accidentally wipe vector/shape state
      // from a layer the rasterize prompt shouldn't have offered on.
      if (node.type !== 'vector' && node.type !== 'shape' && node.type !== 'text') return state;
      return withDocument(
        state,
        updateNode(state.document, action.id, {
          type: 'paint',
          path: undefined,
          vectorSource: undefined,
        }),
      );
    }
    case 'UPDATE_VECTOR_ANCHOR': {
      const node = state.document.layers[action.id];
      if (!node || node.type !== 'vector' || !node.path) return state;
      const subIdx = action.subPathIndex ?? 0;
      const sub = node.path.subPaths[subIdx];
      if (!sub) return state;
      const a = sub.anchors[action.anchorIndex];
      if (!a) return state;
      const nextAnchor = { ...a, ...action.anchor };
      const nextAnchors = sub.anchors.slice();
      nextAnchors[action.anchorIndex] = nextAnchor;
      const nextSubPaths = node.path.subPaths.slice();
      nextSubPaths[subIdx] = { ...sub, anchors: nextAnchors };
      const nextPath = { ...node.path, subPaths: nextSubPaths };
      return withDocument(state, updateNode(state.document, action.id, { path: nextPath }));
    }
    case 'SET_QUICK_MASK_ACTIVE': {
      // Atomic transition: when entering, snapshot the current tool
      // and switch to 'quickmask'. When leaving, restore the
      // snapshotted tool (or fall back to 'marquee' so the user lands
      // on a sensible selection-related tool). No-op when already in
      // the requested state.
      if (action.active === state.quickMaskActive) return state;
      if (action.active) {
        return {
          ...state,
          quickMaskActive: true,
          quickMaskPrevTool: state.activeTool,
          activeTool: 'quickmask',
          // Mirror SET_ACTIVE_TOOL's side effects so toggling Quick
          // Mask from another tool's panel doesn't leave Crop /
          // AI-picker stuck open behind the QM overlay.
          crop: { ...state.crop, isActive: false },
          aiPickerOpen: false,
        };
      }
      return {
        ...state,
        quickMaskActive: false,
        quickMaskPrevTool: null,
        activeTool: state.quickMaskPrevTool ?? 'marquee',
      };
    }
    case 'UPDATE_BRUSH_SETTINGS':
      return { ...state, brushSettings: { ...state.brushSettings, ...action.settings } };
    case 'UPDATE_TEXT_SETTINGS':
      return { ...state, textSettings: { ...state.textSettings, ...action.settings } };
    case 'UPDATE_SHAPE_SETTINGS':
      return { ...state, shapeSettings: { ...state.shapeSettings, ...action.settings } };
    case 'UPDATE_MARQUEE_SETTINGS':
      return { ...state, marqueeSettings: { ...state.marqueeSettings, ...action.settings } };
    case 'UPDATE_RETOUCH_SETTINGS': {
      // Merge top-level fields directly, but spread per-variant
      // nested blocks so a `{ clone: { aligned: false } }` patch
      // updates just `aligned` and keeps `sample` intact. Without
      // this, partial variant updates would clobber each other's
      // unrelated fields.
      const next = { ...state.retouchSettings };
      const patch = action.settings;
      const variantKeys = [
        'clone',
        'heal',
        'smudge',
        'blur',
        'sharpen',
        'dodge',
        'burn',
        'sponge',
      ] as const;
      for (const key of Object.keys(patch) as (keyof RetouchSettings)[]) {
        if ((variantKeys as readonly string[]).includes(key)) {
          // Variant nested-merge.
          const variantPatch = patch[key] as Record<string, unknown>;
          const current = next[key as (typeof variantKeys)[number]];
          (next as Record<string, unknown>)[key] = { ...current, ...variantPatch };
        } else {
          (next as Record<string, unknown>)[key] = patch[key];
        }
      }
      return { ...state, retouchSettings: next };
    }
    case 'SET_ZOOM':
      return { ...state, zoom: Math.max(0.1, Math.min(10, action.zoom)) };
    case 'SET_CANVAS_DIMENSIONS':
      return { ...state, canvasWidth: action.width, canvasHeight: action.height };
    case 'SET_CROP':
      return { ...state, crop: { ...state.crop, ...action.crop } };
    case 'SET_SELECTION':
      return { ...state, selectedObjectIds: action.objectIds };
    case 'SET_FILE_INFO':
      return { ...state, fileName: action.fileName, fileId: action.fileId };
    case 'MARK_DIRTY':
      return { ...state, isDirty: true };
    case 'MARK_CLEAN':
      return { ...state, isDirty: false };
    case 'TOGGLE_PROPERTIES_PANEL':
      return { ...state, propertiesPanelOpen: !state.propertiesPanelOpen };
    case 'TOGGLE_LAYERS_PANEL':
      return { ...state, layersPanelOpen: !state.layersPanelOpen };
    case 'ADD_LAYER': {
      // Tier 0 contract: new layers go to the front of the root
      // list. Tree-aware (parent-into-group) placement comes via
      // a separate action when the LayersPanel rewrite lands.
      const next = addNode(state.document, action.layer, { parentId: null, index: 0 });
      return {
        ...withDocument(state, next),
        activeLayerId: action.setActive ? action.layer.id : state.activeLayerId,
      };
    }
    case 'ADD_ADJUSTMENT': {
      // Placement is caller-controlled: typically the "Apply as
      // adjustment layer" flow inserts the new adjustment directly
      // above the active layer (so `clipToBelow` scopes the effect
      // to just that layer's silhouette); the LayersPanel's "+"
      // entry-point inserts at the root front. The reducer trusts
      // the caller's `parentId`/`index` and lets `addNode` validate
      // them.
      const parentId = action.parentId ?? null;
      const index = action.index ?? 0;
      const node: LayerNode = {
        id: action.layerId,
        name: action.name,
        type: 'adjustment',
        visible: true,
        parentId,
        adjustment: action.config,
        clipToBelow: action.clipToBelow,
      };
      const next = addNode(state.document, node, { parentId, index });
      const setActive = action.setActive ?? true;
      return {
        ...withDocument(state, next),
        activeLayerId: setActive ? action.layerId : state.activeLayerId,
      };
    }
    case 'UPDATE_ADJUSTMENT': {
      // Patch an existing adjustment node's config. No-op if the
      // node isn't an adjustment — wrong target is a caller bug, but
      // dropping silently is friendlier than throwing inside a
      // reducer (reducers should never throw).
      const node = state.document.layers[action.id];
      if (!node || node.type !== 'adjustment') return state;
      return withDocument(
        state,
        updateNode(state.document, action.id, { adjustment: action.config }),
      );
    }
    case 'REMOVE_LAYER': {
      let next = removeNode(state.document, action.id);
      // Cascade: any text layer bound to the removed layer's path
      // gets auto-unbound so we don't carry a dangling pathLayerId
      // through future renders. The text-path-sync effect in
      // EditorPage would refuse to bind to a missing layer anyway,
      // but clearing the field here keeps the data model honest +
      // means Undo restores the binding alongside the path layer
      // it was bound to (no separate "re-bind on undo" step).
      for (const id in next.layers) {
        const node = next.layers[id];
        if (node.type === 'text' && node.textPath?.pathLayerId === action.id) {
          next = updateNode(next, id, { textPath: undefined });
        }
      }
      const ordered = toOrderedList(next);
      return {
        ...withDocument(state, next),
        activeLayerId:
          state.activeLayerId === action.id ? (ordered[0]?.id ?? null) : state.activeLayerId,
      };
    }
    case 'RENAME_LAYER':
      return withDocument(state, updateNode(state.document, action.id, { name: action.name }));
    case 'TOGGLE_LAYER_VISIBILITY': {
      const node = state.document.layers[action.id];
      if (!node) return state;
      return withDocument(state, updateNode(state.document, action.id, { visible: !node.visible }));
    }
    case 'SET_LAYER_OPACITY':
      return withDocument(
        state,
        updateNode(state.document, action.id, {
          opacity: Math.max(0, Math.min(1, action.opacity)),
        }),
      );
    case 'SET_LAYER_BLEND_MODE':
      return withDocument(
        state,
        updateNode(state.document, action.id, { blendMode: action.blendMode }),
      );
    case 'SET_LAYER_EFFECTS':
      return withDocument(
        state,
        updateNode(state.document, action.id, { effects: action.effects }),
      );
    case 'SET_LAYER_MASK':
      return withDocument(state, setMask(state.document, action.id, action.mask));
    case 'SET_MASK_VECTOR_PATH': {
      // Replace the vector path on a vector mask. The canvas-side
      // re-rasterize-and-materialize step happens out-of-band in
      // EditorPage's vector-mask-sync effect (mirrors the
      // raster-mask sync's config-diff pattern). No-op when the
      // layer has no mask, or when the mask's kind isn't 'vector'
      // — caller must CONVERT_MASK_KIND first if switching from
      // raster.
      const node = state.document.layers[action.id];
      if (!node || !node.mask) return state;
      if ((node.mask.kind ?? 'raster') !== 'vector') return state;
      const nextMask: LayerMask = { ...node.mask, vectorPath: action.path };
      return withDocument(state, setMask(state.document, action.id, nextMask));
    }
    case 'CONVERT_MASK_KIND': {
      // Switch a mask's kind. Vector → raster drops vectorPath
      // and leaves the existing rasterized RAW alpha intact on
      // the canvas (the rasterized snapshot becomes the new source
      // of truth — lossless). Raster → vector requires the caller
      // to supply the seeded geometry; the canvas-side
      // setMaskVectorPath then re-rasterizes RAW from the new
      // path. No-op when there's no mask or the kind already
      // matches.
      const node = state.document.layers[action.id];
      if (!node || !node.mask) return state;
      const currentKind = node.mask.kind ?? 'raster';
      if (currentKind === action.kind) return state;
      const nextMask: LayerMask = { ...node.mask, kind: action.kind };
      if (action.kind === 'vector') {
        // Vector kind requires a path; default to an empty
        // sub-path list when caller didn't supply one (renders
        // as fully-hidden — every alpha 0 — until the user adds
        // anchors). Caller normally supplies a traced or seed
        // rect path so this fallback is just defensive.
        nextMask.vectorPath = action.vectorPath ?? { subPaths: [] };
      } else {
        // Raster kind doesn't carry vectorPath. Drop it explicitly
        // so a future ?? lookup doesn't accidentally rasterize a
        // stale path on a raster mask.
        nextMask.vectorPath = undefined;
      }
      return withDocument(state, setMask(state.document, action.id, nextMask));
    }
    case 'SET_TEXT_PATH': {
      const node = state.document.layers[action.id];
      // No-op when the target isn't a text layer — binding text-path
      // semantics to a shape / image / vector would render
      // nothing visible AND would leak the field into a node type
      // whose render path can't honor it.
      if (!node || node.type !== 'text') return state;
      // No-op when trying to bind to a non-existent or non-vector
      // path layer. Caller (TextPanel "Bind to Path") gates the
      // button on multi-select state already, but the reducer
      // re-validates so a stale dispatch can't corrupt the doc.
      if (action.binding !== null) {
        const target = state.document.layers[action.binding.pathLayerId];
        if (!target || target.type !== 'vector') return state;
      }
      return withDocument(
        state,
        updateNode(state.document, action.id, { textPath: action.binding ?? undefined }),
      );
    }
    case 'SET_EDIT_TARGET':
      // No-op the dispatch when the value didn't change so the
      // state object's identity stays stable across redundant
      // toggles (clicking the already-selected thumb should not
      // re-fire downstream effects).
      if ((state.editTargets[action.id] ?? 'layer') === action.target) return state;
      return {
        ...state,
        editTargets: { ...state.editTargets, [action.id]: action.target },
      };
    case 'GROUP_LAYERS':
      return {
        ...withDocument(
          state,
          groupNodes(state.document, action.ids, { id: action.groupId, name: action.name }),
        ),
        activeLayerId: action.groupId,
      };
    case 'MOVE_NODE_IN_TREE': {
      // Validation lives in `moveNode` (cycle check, parent type
      // check). We catch the throw here and return state unchanged
      // — invalid drops shouldn't crash the reducer.
      try {
        const next = moveNode(state.document, action.id, {
          parentId: action.parentId,
          index: action.index,
        });
        return withDocument(state, next);
      } catch {
        return state;
      }
    }
    case 'UNGROUP': {
      const next = ungroupNode(state.document, action.id);
      // ungroupNode short-circuits when the id isn't a group; if
      // the doc reference is unchanged, skip rebuilding state.
      if (next === state.document) return state;
      // If the active layer was the now-removed group, fall back
      // to the first child (which is now where the group used to
      // be in render order). Worth handling because the user
      // typically calls Ungroup with the group itself selected.
      const wasActive = state.activeLayerId === action.id;
      const firstChildId = state.document.layers[action.id]?.childIds?.[0] ?? null;
      return {
        ...withDocument(state, next),
        activeLayerId: wasActive ? firstChildId : state.activeLayerId,
      };
    }
    case 'SET_ACTIVE_LAYER':
      return { ...state, activeLayerId: action.id };
    case 'REORDER_LAYER':
      return withDocument(
        state,
        reorderFlattened(state.document, action.fromIndex, action.toIndex),
      );
    case 'RESET_LAYERS':
      return {
        ...withDocument(state, action.document),
        activeLayerId: action.activeLayerId,
      };
    case 'RESET_LAYERS_FLAT':
      return {
        ...withDocument(state, fromFlatLayers(action.layers)),
        activeLayerId: action.activeLayerId,
      };
    default:
      return state;
  }
}

export function useImageEditorState() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setActiveTool = useCallback(
    (tool: EditorTool) => dispatch({ type: 'SET_ACTIVE_TOOL', tool }),
    [],
  );
  const setSelectedAITool = useCallback(
    (tool: AICapability | null) => dispatch({ type: 'SET_SELECTED_AI_TOOL', tool }),
    [],
  );
  const setAIPickerOpen = useCallback(
    (open: boolean) => dispatch({ type: 'SET_AI_PICKER_OPEN', open }),
    [],
  );
  const setQuickMaskActive = useCallback(
    (active: boolean) => dispatch({ type: 'SET_QUICK_MASK_ACTIVE', active }),
    [],
  );
  const setQuickMaskMode = useCallback(
    (mode: 'add' | 'subtract') => dispatch({ type: 'SET_QUICK_MASK_MODE', mode }),
    [],
  );
  const beginTransform = useCallback(
    (layerId: string) => dispatch({ type: 'BEGIN_TRANSFORM', layerId }),
    [],
  );
  const setTransformMode = useCallback(
    (mode: 'free' | 'skew' | 'distort' | 'perspective' | 'warp') =>
      dispatch({ type: 'SET_TRANSFORM_MODE', mode }),
    [],
  );
  const convertLayerToPaint = useCallback(
    (id: string) => dispatch({ type: 'CONVERT_LAYER_TO_PAINT', id }),
    [],
  );
  const endTransform = useCallback(
    (apply: boolean) => dispatch({ type: 'END_TRANSFORM', apply }),
    [],
  );
  const updatePenSettings = useCallback(
    (settings: Partial<PenSettings>) => dispatch({ type: 'UPDATE_PEN_SETTINGS', settings }),
    [],
  );
  const beginPathEdit = useCallback(
    (layerId: string, target?: 'layer' | 'mask') =>
      dispatch({ type: 'BEGIN_PATH_EDIT', layerId, target }),
    [],
  );
  const setPathEditAnchor = useCallback(
    (index: number | null) => dispatch({ type: 'SET_PATH_EDIT_ANCHOR', index }),
    [],
  );
  const endPathEdit = useCallback(
    (apply: boolean) => dispatch({ type: 'END_PATH_EDIT', apply }),
    [],
  );
  const setVectorPath = useCallback(
    (id: string, path: VectorPath, options?: { clearVectorSource?: boolean }) =>
      dispatch({
        type: 'SET_VECTOR_PATH',
        id,
        path,
        clearVectorSource: options?.clearVectorSource,
      }),
    [],
  );
  const updateVectorAnchor = useCallback(
    (id: string, anchorIndex: number, anchor: Partial<VectorAnchor>, subPathIndex?: number) =>
      dispatch({
        type: 'UPDATE_VECTOR_ANCHOR',
        id,
        anchorIndex,
        subPathIndex,
        anchor,
      }),
    [],
  );
  const updateBrushSettings = useCallback(
    (settings: Partial<BrushSettings>) => dispatch({ type: 'UPDATE_BRUSH_SETTINGS', settings }),
    [],
  );
  const updateTextSettings = useCallback(
    (settings: Partial<TextSettings>) => dispatch({ type: 'UPDATE_TEXT_SETTINGS', settings }),
    [],
  );
  const updateShapeSettings = useCallback(
    (settings: Partial<ShapeSettings>) => dispatch({ type: 'UPDATE_SHAPE_SETTINGS', settings }),
    [],
  );
  const updateMarqueeSettings = useCallback(
    (settings: Partial<MarqueeSettings>) => dispatch({ type: 'UPDATE_MARQUEE_SETTINGS', settings }),
    [],
  );
  const updateRetouchSettings = useCallback(
    (settings: RetouchSettingsPatch) => dispatch({ type: 'UPDATE_RETOUCH_SETTINGS', settings }),
    [],
  );
  const setZoom = useCallback((zoom: number) => dispatch({ type: 'SET_ZOOM', zoom }), []);
  const setCanvasDimensions = useCallback(
    (width: number, height: number) => dispatch({ type: 'SET_CANVAS_DIMENSIONS', width, height }),
    [],
  );
  const setCrop = useCallback(
    (crop: Partial<CropState>) => dispatch({ type: 'SET_CROP', crop }),
    [],
  );
  const setSelection = useCallback(
    (objectIds: string[]) => dispatch({ type: 'SET_SELECTION', objectIds }),
    [],
  );
  const setFileInfo = useCallback(
    (fileName: string, fileId: string | null) =>
      dispatch({ type: 'SET_FILE_INFO', fileName, fileId }),
    [],
  );
  const markDirty = useCallback(() => dispatch({ type: 'MARK_DIRTY' }), []);
  const markClean = useCallback(() => dispatch({ type: 'MARK_CLEAN' }), []);
  const togglePropertiesPanel = useCallback(
    () => dispatch({ type: 'TOGGLE_PROPERTIES_PANEL' }),
    [],
  );
  const toggleLayersPanel = useCallback(() => dispatch({ type: 'TOGGLE_LAYERS_PANEL' }), []);
  const addLayer = useCallback(
    (layer: Layer, setActive = true) => dispatch({ type: 'ADD_LAYER', layer, setActive }),
    [],
  );
  const addAdjustment = useCallback(
    (
      layerId: string,
      name: string,
      config: AdjustmentConfig,
      options?: {
        parentId?: string | null;
        index?: number;
        clipToBelow?: boolean;
        setActive?: boolean;
      },
    ) =>
      dispatch({
        type: 'ADD_ADJUSTMENT',
        layerId,
        name,
        config,
        parentId: options?.parentId,
        index: options?.index,
        clipToBelow: options?.clipToBelow,
        setActive: options?.setActive ?? true,
      }),
    [],
  );
  const updateAdjustment = useCallback(
    (id: string, config: AdjustmentConfig) => dispatch({ type: 'UPDATE_ADJUSTMENT', id, config }),
    [],
  );
  const removeLayer = useCallback((id: string) => dispatch({ type: 'REMOVE_LAYER', id }), []);
  const renameLayer = useCallback(
    (id: string, name: string) => dispatch({ type: 'RENAME_LAYER', id, name }),
    [],
  );
  const toggleLayerVisibility = useCallback(
    (id: string) => dispatch({ type: 'TOGGLE_LAYER_VISIBILITY', id }),
    [],
  );
  const setLayerOpacity = useCallback(
    (id: string, opacity: number) => dispatch({ type: 'SET_LAYER_OPACITY', id, opacity }),
    [],
  );
  const setLayerBlendMode = useCallback(
    (id: string, blendMode: BlendMode) => dispatch({ type: 'SET_LAYER_BLEND_MODE', id, blendMode }),
    [],
  );
  const setLayerEffects = useCallback(
    (id: string, effects: LayerEffects) => dispatch({ type: 'SET_LAYER_EFFECTS', id, effects }),
    [],
  );
  const setLayerMask = useCallback(
    (id: string, mask: LayerMask | null) => dispatch({ type: 'SET_LAYER_MASK', id, mask }),
    [],
  );
  const setMaskVectorPath = useCallback(
    (id: string, path: VectorPath) => dispatch({ type: 'SET_MASK_VECTOR_PATH', id, path }),
    [],
  );
  const convertMaskKind = useCallback(
    (id: string, kind: 'raster' | 'vector', vectorPath?: VectorPath) =>
      dispatch({ type: 'CONVERT_MASK_KIND', id, kind, vectorPath }),
    [],
  );
  const setTextPath = useCallback(
    (id: string, binding: TextPathBinding | null) =>
      dispatch({ type: 'SET_TEXT_PATH', id, binding }),
    [],
  );
  const setEditTarget = useCallback(
    (id: string, target: 'layer' | 'mask') => dispatch({ type: 'SET_EDIT_TARGET', id, target }),
    [],
  );
  const groupLayers = useCallback(
    (ids: string[], groupId: string, name?: string) =>
      dispatch({ type: 'GROUP_LAYERS', ids, groupId, name }),
    [],
  );
  const ungroup = useCallback((id: string) => dispatch({ type: 'UNGROUP', id }), []);
  const moveNodeInTree = useCallback(
    (id: string, parentId: string | null, index: number) =>
      dispatch({ type: 'MOVE_NODE_IN_TREE', id, parentId, index }),
    [],
  );
  const setActiveLayer = useCallback(
    (id: string | null) => dispatch({ type: 'SET_ACTIVE_LAYER', id }),
    [],
  );
  const reorderLayer = useCallback(
    (fromIndex: number, toIndex: number) => dispatch({ type: 'REORDER_LAYER', fromIndex, toIndex }),
    [],
  );
  const resetLayers = useCallback(
    (layers: Layer[], activeLayerId: string | null = layers[0]?.id ?? null) =>
      dispatch({ type: 'RESET_LAYERS_FLAT', layers, activeLayerId }),
    [],
  );
  const resetLayerDocument = useCallback(
    (document: EditorDocument, activeLayerId: string | null = document.rootIds[0] ?? null) =>
      dispatch({ type: 'RESET_LAYERS', document, activeLayerId }),
    [],
  );

  return {
    state,
    dispatch,
    setActiveTool,
    setSelectedAITool,
    setAIPickerOpen,
    setQuickMaskActive,
    setQuickMaskMode,
    beginTransform,
    setTransformMode,
    convertLayerToPaint,
    endTransform,
    updatePenSettings,
    beginPathEdit,
    setPathEditAnchor,
    endPathEdit,
    setVectorPath,
    updateVectorAnchor,
    updateBrushSettings,
    updateTextSettings,
    updateShapeSettings,
    updateMarqueeSettings,
    updateRetouchSettings,
    setZoom,
    setCanvasDimensions,
    setCrop,
    setSelection,
    setFileInfo,
    markDirty,
    markClean,
    togglePropertiesPanel,
    toggleLayersPanel,
    addLayer,
    addAdjustment,
    updateAdjustment,
    removeLayer,
    renameLayer,
    toggleLayerVisibility,
    setLayerOpacity,
    setLayerBlendMode,
    setLayerEffects,
    setLayerMask,
    setMaskVectorPath,
    convertMaskKind,
    setTextPath,
    setEditTarget,
    groupLayers,
    ungroup,
    moveNodeInTree,
    setActiveLayer,
    reorderLayer,
    resetLayers,
    resetLayerDocument,
  };
}

export { initialState, reducer };
